import React, { useState } from "react";
import { BaseImportExportModalComponent } from "../../../components";
import { DataTypes, useSmokeballApp } from "../../../shared";
import { getRegionFromURL } from "../../../shared/utils/region";
import {
  getTimeFeesDisbursementsRegionSpecificLabel,
  PRODUCT_TIERS,
} from "../../../shared/utils/constants";
import log from "../../../shared/utils/log";
import { useNewMigration, useSubmitMigration } from "../../../hooks";
import { useNavigate } from "react-router-dom";

export interface ExportOption {
  label: string;
  description?: string;
  dataTypes: string[];
  selected: boolean;
  subSection?: {
    label?: string;
    description?: string[];
    toggleValue?: boolean;
    toggleDisabled?: boolean;
    onToggle?: (value: boolean) => void;
  };
}

export const ExportTypeSelectionModal: React.FC<{
  closeModal: () => void;
  productId: string | undefined;
  titleText: string;
  subtitleText: string;
  exportReason?: string;
}> = ({ closeModal, productId, titleText, subtitleText, exportReason }) => {
  const navigate = useNavigate();
  const region = getRegionFromURL();
  const [paidInvoicesToggle, setPaidInvoicesToggle] = useState(false);
  const [dataTypesToShow, setDataTypesToShow] = useState<string[]>([]);
  const [dataTypeSubOptionsToShow, setDataTypeSubOptionsToShow] = useState<
    string[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const { auth, context } = useSmokeballApp();

  const { handleNewMigrationClick, isLoading: isInitialising } =
    useNewMigration();
  const { handleSubmitMigrationClick, isLoading: isSubmitting } =
    useSubmitMigration({
      accountId: context?.firmId || "",
      token: auth?.token,
    });

  // Matter relationships always needs to be associated with matters but should only be shown for Grow or Prosper clients
  const isGrowOrProsper =
    productId === PRODUCT_TIERS.GROW || productId === PRODUCT_TIERS.PROSPER;

  // Shared handler to ensure "Paid Invoices" logic is consistent across selection methods
  const updatePaidInvoicesToggle = (nextValue: boolean) => {
    setPaidInvoicesToggle(nextValue);

    // If ON, add it; if OFF, remove it
    setDataTypeSubOptionsToShow((prev) => {
      const key = "paidInvoices";
      if (nextValue && !prev.includes(key)) {
        return [...prev, key];
      } else if (!nextValue) {
        return prev.filter((x) => x !== key);
      }
      return prev;
    });

    // Sync UI state in the options array
    setOptions((prev) => {
      return prev.map((opt) => {
        // Early termination for non-invoice options
        if (opt.label !== DataTypes.Invoices) {
          return opt;
        }

        return {
          ...opt,
          subSection: {
            ...opt.subSection!,
            toggleValue: nextValue,
          },
        };
      });
    });
  };

  const [options, setOptions] = useState<ExportOption[]>(() => {
    // Initialise options with region and tier logic
    const initialOptions: ExportOption[] = [
      {
        label: DataTypes.Staff,
        selected: false,
        dataTypes: [DataTypes.Staff],
      },
      {
        label: DataTypes.Contacts,
        selected: false,
        dataTypes: [DataTypes.Contacts],
      },
      {
        label: DataTypes.Matters,
        selected: false,
        dataTypes: [
          DataTypes.Matters,
          ...(isGrowOrProsper ? [DataTypes.MatterRelationships] : []),
        ],
      },
      // {
      //   label: DataTypes.OpeningBalances,
      //   selected: false,
      //   dataTypes: [DataTypes.OpeningBalances],
      // },
      {
        label: getTimeFeesDisbursementsRegionSpecificLabel(region),
        selected: false,
        dataTypes: [
          // DataTypes.ActivityTypes,
          DataTypes.UnbilledTimeAndFees,
          DataTypes.UnbilledDisbursements,
        ],
      },
      {
        label: DataTypes.Invoices,
        description: "Includes line items",
        selected: false,
        dataTypes: [DataTypes.Invoices, DataTypes.InvoiceItems],
        subSection: {
          label: "Paid Invoices",
          description: [
            "Unpaid and partially paid invoices are always included.",
            "Toggle to also include fully paid invoices.",
          ],
          toggleValue: false,
          toggleDisabled: true,
          onToggle: () => {}, // Toggle disabled and not checked
        },
      },
      // Add Memos and Tasks only for Boost/Grow/Prosper tiers
      ...(productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
        ? [
            {
              label: DataTypes.Memos,
              selected: false,
              dataTypes: [DataTypes.Memos],
            },
            {
              label: DataTypes.Tasks,
              selected: false,
              dataTypes: [DataTypes.Tasks],
            },
          ]
        : []),

      // // Add Safe Custody Packets and Documents only for Grow/Prosper tiers and non-US regions
      // ...((productId === PRODUCT_TIERS.GROW ||
      //   productId === PRODUCT_TIERS.PROSPER) &&
      // region !== "US"
      //   ? [
      //       {
      //         label: "Safe Custody Packets and Documents",
      //         selected: false,
      //         dataTypes: [
      //           DataTypes.SafeCustodyPackets,
      //           DataTypes.SafeCustodyDocuments,
      //         ],
      //       },
      //     ]
      //   : []),
    ];

    return initialOptions;
  });

  const handleOptionChange = (index: number) => {
    const newOptions = [...options];
    newOptions[index].selected = !newOptions[index].selected;

    const selectedOption = newOptions[index];

    // Invoice toggle logic
    if (selectedOption.label === DataTypes.Invoices) {
      if (selectedOption.selected) {
        // If invoices selected then enable toggle (which is off by default)
        setPaidInvoicesToggle(false);

        newOptions[index].subSection = {
          ...newOptions[index].subSection,
          label: newOptions[index].subSection?.label ?? "Paid Invoices",
          description: newOptions[index].subSection?.description,
          toggleValue: false,
          toggleDisabled: false,
          onToggle: updatePaidInvoicesToggle,
        };
      } else {
        // Deselect will disable and remove dataTypeSubOptions
        setPaidInvoicesToggle(false);

        newOptions[index].subSection = {
          ...newOptions[index].subSection,
          label: "Paid Invoices",
          description: newOptions[index].subSection?.description,
          toggleValue: false,
          toggleDisabled: true,
          onToggle: () => {}, // Toggle disabled and not checked
        };

        // Remove from dataTypeSubOptions
        setDataTypeSubOptionsToShow((prev) =>
          prev.filter((x) => x !== "paidInvoices"),
        );
      }
    }

    // If the option is selected, add its cards to the list
    if (selectedOption.selected) {
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = [...prevDataTypes];
        selectedOption.dataTypes.forEach((dataType) => {
          if (!newDataTypes.includes(dataType)) {
            newDataTypes.push(dataType); // Add the card if it's not already in the list
          }
        });
        return newDataTypes;
      });
    } else {
      // If the option is deselected, check if any other selected options require this option's export types
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = prevDataTypes.filter(
          (dataType) =>
            !selectedOption.dataTypes.includes(dataType) ||
            // Keep export types that are still required by other selected options
            newOptions.some(
              (option) =>
                option.selected && option.dataTypes.includes(dataType),
            ),
        );
        return newDataTypes;
      });
    }

    setOptions(newOptions);
  };

  const handleUnselectAll = () => {
    const resetOptions = options.map((option) => {
      if (option.label === DataTypes.Invoices && option.subSection) {
        // Reset invoices to default unselected/off/disabled
        return {
          ...option,
          selected: false,
          subSection: {
            ...option.subSection,
            toggleValue: false,
            toggleDisabled: true,
          },
        };
      }
      // All others just deselect
      return { ...option, selected: false };
    });

    setOptions(resetOptions);
    setDataTypesToShow([]); // Clear export types when unselecting all
    setDataTypeSubOptionsToShow([]); // Clear dataTypeSubOptions when unselecting all
    setPaidInvoicesToggle(false); // Set paid invoices toggle to false when unselecting all
  };

  const handleSelectAll = () => {
    // Always show these export types for all tiers
    const selectAllDataTypes = [
      DataTypes.Staff,
      DataTypes.Contacts,
      DataTypes.Matters,
      ...(isGrowOrProsper ? [DataTypes.MatterRelationships] : []),
      // DataTypes.OpeningBalances,
      // DataTypes.ActivityTypes,
      DataTypes.UnbilledTimeAndFees,
      DataTypes.UnbilledDisbursements,
      DataTypes.Invoices,
      DataTypes.InvoiceItems,
      
      // Add region and tier specific types
      ...(productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
        ? [DataTypes.Memos, DataTypes.Tasks]
        : []),

      // ...(isGrowOrProsper && region !== "US"
      //   ? [DataTypes.SafeCustodyPackets, DataTypes.SafeCustodyDocuments]
      //   : []),
    ];

    // Build updated options with invoice toggle logic
    const newOptions = options.map((option) => {
      if (option.label === DataTypes.Invoices) {
        const wasSelected = option.selected;
        const prevToggleValue = option.subSection?.toggleValue ?? false;

        return {
          ...option,
          selected: true,
          subSection: {
            ...option.subSection,
            toggleDisabled: false,
            // Keep toggle state if already selected, otherwise default to false
            toggleValue: wasSelected ? prevToggleValue : false,
            onToggle: updatePaidInvoicesToggle, // Link functional handler
          },
        };
      }

      // All others just select
      return { ...option, selected: true };
    });

    // Apply updates
    setOptions(newOptions);
    setDataTypesToShow(selectAllDataTypes);

    // If a dataTypeSubOption was already selected then keep it in state
    if (
      paidInvoicesToggle &&
      !dataTypeSubOptionsToShow.includes("paidInvoices")
    ) {
      setDataTypeSubOptionsToShow((prev) => [...prev, "paidInvoices"]);
    }
  };

  const handleProceed = async () => {
    setIsLoading(true);
    log("Export Initialising:", isInitialising);

    try {
      // Ensure selected types and dataTypeSubOptions are unique
      const uniqueDataTypes = [...new Set(dataTypesToShow)];
      const uniqueDataTypeSubOptions = [...new Set(dataTypeSubOptionsToShow)];

      const payload = {
        dataTypes: uniqueDataTypes,
        dataTypeSubOptions: uniqueDataTypeSubOptions,
      };

      log("Final export payload:", payload);

      // Initialise the migration, passing "smokeball-export" as the source system
      const sourceSystem = "smokeball-export";
      const migrationSetId = await handleNewMigrationClick({
        selectedImportTypes: payload.dataTypes,
        dataTypeSubOptions: payload.dataTypeSubOptions,
        sourceSystem,
        exportReason,
      });

      if (!migrationSetId) {
        console.error("Failed to initialize export migration.");
        return;
      }

      log("Export Migration Initialised with ID:", migrationSetId);

      // Immediately submit the migration for export processing
      await handleSubmitMigrationClick(migrationSetId.toString());
      closeModal();
      log("Export is being submitted: ", isSubmitting);
      // Navigate back to main page
      navigate("/embedded/jumpstart");
    } catch (error) {
      console.error("Error during export process:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseImportExportModalComponent
      titleText={titleText}
      subtitleText={subtitleText}
      closeModal={closeModal}
      isLoading={isLoading}
      options={options}
      onToggleOption={handleOptionChange}
      onSelectAll={handleSelectAll}
      onUnselectAll={handleUnselectAll}
      onProceed={handleProceed}
    />
  );
};
